import { alpha, Box } from '@mui/material';
import { BASE_COLORS } from 'lib/functions/const/colors';
import { MobileSubtitle } from 'lib/mobileDesignSystem';
import React from 'react';
import { useNavigate } from 'react-router-dom';

/** Variante de card por color (alineado con chips/BASE_COLORS). */
export type HomeProductCardVariant =
  | 'primary'
  | 'success'
  | 'warning'
  | 'error'
  | 'info'
  | 'default'
  | 'assigned'
  | 'reviewed'
  | 'due';

const VARIANT_COLORS: Record<HomeProductCardVariant, string> = {
  primary: BASE_COLORS.blue,
  success: BASE_COLORS.green,
  warning: BASE_COLORS.yellow,
  error: BASE_COLORS.red,
  info: BASE_COLORS.lightBlue,
  default: BASE_COLORS.gray,
  assigned: BASE_COLORS.teal,
  reviewed: BASE_COLORS.darkGreen,
  due: BASE_COLORS.burgundy,
};

export const getHomeProductCardColor = (
  variant: HomeProductCardVariant,
): string => VARIANT_COLORS[variant] ?? VARIANT_COLORS.default;

export interface HomeProductCardProps {
  title: string;
  icon: React.ReactNode;
  to: string;
  /** Variante de color (fade y acento del icono). Por defecto: primary. */
  variant?: HomeProductCardVariant;
  width?: number;
}

const CARD_WIDTH = 150;
const CARD_HEIGHT = 100;

const HomeProductCard: React.FC<HomeProductCardProps> = ({
  title,
  icon,
  to,
  variant = 'primary',
  width = CARD_WIDTH,
}) => {
  const navigate = useNavigate();
  const color = getHomeProductCardColor(variant);

  return (
    <Box
      component="button"
      type="button"
      onClick={() => navigate(to)}
      sx={{
        width,
        minWidth: width,
        minHeight: CARD_HEIGHT,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 2,
        p: 2.5,
        border: 'none',
        borderRadius: 2.5,
        background: (theme) =>
          `linear-gradient(to top, ${alpha(color, 0.04)} 0%, ${alpha(color, 0.01)} 50%, ${theme.palette.background.paper} 100%)`,
        cursor: 'pointer',
        textAlign: 'left',
        boxShadow: (theme) =>
          `0 1px 3px ${alpha(theme.palette.common.black, 0.06)}, 0 1px 2px ${alpha(theme.palette.common.black, 0.04)}`,
        transition: 'box-shadow 0.2s ease',
        '&:hover': {
          boxShadow: (theme) =>
            `0 4px 12px ${alpha(theme.palette.common.black, 0.08)}, 0 2px 4px ${alpha(theme.palette.common.black, 0.04)}`,
        },
        '&:active': {
          boxShadow: (theme) =>
            `0 1px 2px ${alpha(theme.palette.common.black, 0.06)}`,
          backgroundColor: (theme) => alpha(theme.palette.action.hover, 0.03),
        },
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: alpha(color, 0.07),
          color: alpha(color, 0.75),
        }}
      >
        {icon}
      </Box>
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          minWidth: 0,
          overflow: 'hidden',
        }}
      >
        <MobileSubtitle
          component="span"
          sx={{
            display: 'block',
            color: 'text.secondary',
            fontWeight: 600,
            fontSize: '1rem',
            lineHeight: 1.3,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {title}
        </MobileSubtitle>
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            width: 32,
            pointerEvents: 'none',
            background: (theme) =>
              `linear-gradient(to right, ${alpha(theme.palette.background.paper, 0)} 0%, ${theme.palette.background.paper} 100%)`,
          }}
        />
      </Box>
    </Box>
  );
};

export default HomeProductCard;
